import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Chip,
    IconButton,
    Typography
} from "@mui/material";
import {
    AssessmentFilterStatus,
    AssessmentFilterStatusText,
    IssueFilter
} from '../../../lib/dashboard/types/IssueFilter';
import {
    Location,
    Tag
} from '../../../API';
import React, {
    useEffect,
    useMemo,
    useState
} from "react";

import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { DateRange } from 'react-date-range';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { IssueStatus } from '../../../models';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import { ResponsivePopover } from '../../general/ResponsivePopover';
import TagIcon from '@mui/icons-material/Tag';
import _ from 'lodash';
import { issueStatusText } from '../../../componentsV2/issue/issuesStatusText';
import { startOfToday } from 'date-fns';

type ListIssuesFilterProps = {
    filter: IssueFilter;
    setFilter: (filter: IssueFilter) => void;
    locationOptions: Array<Location>;
    earliestDate: Date;
    tagOptions: Array<Tag>;
};

export const ListIssuesFilter = (props: ListIssuesFilterProps) => {
    const { filter, setFilter, locationOptions, earliestDate, tagOptions } = props;
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    const [filterDraft, setFilterDraft] = useState<IssueFilter>({ ...filter });
    const [focusedFilter, setFocusedFilter] = useState<keyof IssueFilter | null>(null);

    const toggleAccordion = (filter: keyof IssueFilter) => {
        setFocusedFilter(prev => prev === filter ? null : filter);
    };

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const hasChanges = useMemo(() => {
        return !_.isEqual(filter, filterDraft);
    }, [filter, filterDraft]);

    useEffect(() => {
        setFilterDraft({ ...filter });
    }, [filter]);

    const handleFilterChange = (key: keyof IssueFilter, value: any) => {
        setFilterDraft(prev => ({
            ...prev,
            [key]: value
        }));
    };

    const handleApplyFilters = () => {
        setFilter(filterDraft);
        handleClose();
    };

    const handleResetFilters = () => {
        setFilterDraft({});
    };

    return (
        <>
            <Button
                onClick={handleClick}
                variant="outlined"
                startIcon={<FilterAltOutlinedIcon />}
                color="inherit"
                sx={{ textTransform: "none" }}
            >
                Filter
            </Button>
            <ResponsivePopover
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left"
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "left"
                }}
                slotProps={{
                    paper: {
                        sx: {
                            maxWidth: { xs: 330, sm: 400 },
                            mt: 1,
                        }
                    }
                }}
                keepMounted
            >
                <Box p={{ xs: 0.5, sm: 1 }} display="flex" flexDirection="column" gap={2} overflow="auto">
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6">Filters</Typography>
                        <IconButton onClick={handleClose} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    <Box
                        display="flex"
                        flexDirection="column"
                        gap={1}
                        maxHeight={{ sm: "calc(50dvh - 80px)" }}
                        sx={{ overflowY: "auto" }}
                        p={0.25}
                    >
                        {/* Date Filter */}
                        <Accordion expanded={focusedFilter === 'date'} onChange={() => toggleAccordion('date')}>
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                sx={{
                                    '& .MuiAccordionSummary-content': {
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1
                                    }
                                }}
                            >
                                <CalendarMonthOutlinedIcon fontSize="small" />
                                <Typography variant="subtitle2">Date Range</Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ display: "flex", justifyContent: "center", pt: 0 }}>
                                <DateRange
                                    ranges={[{
                                        startDate: filterDraft.date?.start || new Date(),
                                        endDate: filterDraft.date?.end || new Date(),
                                        key: 'selection'
                                    }]}
                                    onChange={item => {
                                        if (item.selection) {
                                            handleFilterChange('date', {
                                                start: item.selection.startDate,
                                                end: item.selection.endDate
                                            });
                                        }
                                    }}
                                    minDate={earliestDate}
                                    maxDate={startOfToday()}
                                />
                            </AccordionDetails>
                        </Accordion>

                        {/* Status Filter */}
                        <Accordion expanded={focusedFilter === 'statuses'} onChange={() => toggleAccordion('statuses')}>
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                sx={{
                                    '& .MuiAccordionSummary-content': {
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1
                                    }
                                }}
                            >
                                <InfoOutlinedIcon fontSize="small" />
                                <Typography variant="subtitle2">Status</Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ pt: 0 }}>
                                <Box display="flex" flexDirection="column" gap={2}>
                                    <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
                                        <Typography variant="subtitle2">Assessments: </Typography>
                                        {Object.values(AssessmentFilterStatus).map(status => (
                                            <Chip
                                                key={status}
                                                label={AssessmentFilterStatusText[status as AssessmentFilterStatus]}
                                                onClick={() => {
                                                    const currentStatuses = filterDraft.statuses || [];
                                                    const newStatuses = currentStatuses.includes(status)
                                                        ? currentStatuses.filter(s => s !== status)
                                                        : [...currentStatuses, status];
                                                    handleFilterChange('statuses', newStatuses);
                                                }}
                                                color={filterDraft.statuses?.includes(status) ? 'primary' : 'default'}
                                                variant={filterDraft.statuses?.includes(status) ? 'filled' : 'outlined'}
                                            />
                                        ))}
                                    </Box>
                                    <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
                                        <Typography variant="subtitle2">Issues: </Typography>
                                        {Object.values(IssueStatus).map(status => (
                                            <Chip
                                                key={status}
                                                label={issueStatusText[status as IssueStatus]}
                                                onClick={() => {
                                                    const currentStatuses = filterDraft.statuses || [];
                                                    const newStatuses = currentStatuses.includes(status)
                                                        ? currentStatuses.filter(s => s !== status)
                                                        : [...currentStatuses, status];
                                                    handleFilterChange('statuses', newStatuses);
                                                }}
                                                color={filterDraft.statuses?.includes(status) ? 'primary' : 'default'}
                                                variant={filterDraft.statuses?.includes(status) ? 'filled' : 'outlined'}
                                            />
                                        ))}
                                    </Box>
                                </Box>
                            </AccordionDetails>
                        </Accordion>

                        {/* Location Filter */}
                        <Accordion expanded={focusedFilter === 'locationIds'} onChange={() => toggleAccordion('locationIds')} >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                sx={{
                                    '& .MuiAccordionSummary-content': {
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1
                                    }
                                }}
                            >
                                <PlaceOutlinedIcon fontSize="small" />
                                <Typography variant="subtitle2">Location</Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ pt: 0 }}>
                                <Box display="flex" flexWrap="wrap" gap={1}>
                                    {locationOptions.map(location => (
                                        <Chip
                                            key={location.id}
                                            label={location.name}
                                            onClick={() => {
                                                const currentLocationIds = filterDraft.locationIds || [];
                                                const newLocationIds = currentLocationIds.includes(location.id)
                                                    ? currentLocationIds.filter(id => id !== location.id)
                                                    : [...currentLocationIds, location.id];
                                                handleFilterChange('locationIds', newLocationIds);
                                            }}
                                            color={filterDraft.locationIds?.includes(location.id) ? 'primary' : 'default'}
                                            variant={filterDraft.locationIds?.includes(location.id) ? 'filled' : 'outlined'}
                                            sx={{ minWidth: "10ch" }}
                                        />
                                    ))}
                                </Box>
                            </AccordionDetails>
                        </Accordion>

                        {/* Tags Filter */}
                        <Accordion expanded={focusedFilter === 'tagIds'} onChange={() => toggleAccordion('tagIds')}>
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                sx={{
                                    '& .MuiAccordionSummary-content': {
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1
                                    }
                                }}
                            >
                                <TagIcon fontSize="small" />
                                <Typography variant="subtitle2">Tags</Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ pt: 0 }}>
                                <Box display="flex" flexWrap="wrap" gap={1}>
                                    {tagOptions.map(tag => (
                                        <Chip
                                            key={tag.id}
                                            label={tag.name}
                                            onClick={() => {
                                                const currentTags = filterDraft.tagIds || [];
                                                const newTags = currentTags.includes(tag.id)
                                                    ? currentTags.filter(id => id !== tag.id)
                                                    : [...currentTags, tag.id];
                                                handleFilterChange('tagIds', newTags);
                                            }}
                                            color={filterDraft.tagIds?.includes(tag.id) ? 'primary' : 'default'}
                                            variant={filterDraft.tagIds?.includes(tag.id) ? 'filled' : 'outlined'}
                                            sx={{ minWidth: "10ch" }}
                                        />
                                    ))}
                                </Box>
                            </AccordionDetails>
                        </Accordion>
                    </Box>
                    {/* Action Buttons */}
                    <Box display="flex" justifyContent="space-between">
                        <Button
                            onClick={handleResetFilters}
                            variant="outlined"
                            color="error"
                            sx={{ textTransform: "none" }}
                        >
                            Reset
                        </Button>
                        <Button
                            onClick={handleApplyFilters}
                            variant="contained"
                            color="primary"
                            disabled={!hasChanges}
                            sx={{ textTransform: "none" }}
                        >
                            Apply Filters
                        </Button>
                    </Box>
                </Box>
            </ResponsivePopover>
        </>
    );
};